import { Button as BaseButton } from "@base-ui/react/button";
import type { LucideIcon } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "./icon";

/**
 * The app's buttons, on Base UI's Button (plan D03).
 *
 * `primary` is A2's `a-primary`: the D11 ember (#D63F14) with white text,
 * 4.6:1. `secondary` is a raised surface, `ghost` has no fill until pressed,
 * `danger` is the destructive fill. Sizes are the touch floor (48 px) and the
 * comfortable primary (56 px); nothing smaller is offered, because nothing
 * smaller passes as a thumb target.
 *
 * A2's `a-press` is on every variant: the 0.97 scale on `:active` is the only
 * press feedback a touch screen gets.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "md" | "lg";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "a-primary",
  secondary: "bg-surface text-ink shadow",
  ghost: "text-ink hover:bg-wash",
  danger: "bg-danger text-on-danger",
};

const SIZE: Record<ButtonSize, string> = {
  md: "h-12 px-5 text-base",
  lg: "h-14 px-6 text-lg",
};

const BASE =
  "a-press inline-flex shrink-0 select-none items-center justify-center gap-2 rounded-full font-bold data-disabled:cursor-not-allowed data-disabled:opacity-40";

export interface ButtonProps
  extends Omit<ComponentProps<typeof BaseButton>, "className" | "children"> {
  children?: ReactNode;
  className?: string;
  /** A leading icon, drawn through `<Icon>`. */
  icon?: LucideIcon;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

export function Button({
  variant,
  size,
  icon,
  className,
  children,
  type,
  ...rest
}: ButtonProps) {
  return (
    <BaseButton
      className={cx(
        BASE,
        VARIANT[variant ?? "primary"],
        SIZE[size ?? "md"],
        className
      )}
      type={type ?? "button"}
      {...rest}
    >
      {icon ? <Icon icon={icon} size={size === "lg" ? 24 : 20} /> : null}
      {children}
    </BaseButton>
  );
}

const ICON_SIZE: Record<ButtonSize, string> = {
  md: "size-12",
  lg: "size-14",
};

export interface IconButtonProps
  extends Omit<ButtonProps, "children" | "icon" | "aria-label"> {
  icon: LucideIcon;
  /** Required: an icon button has no text, so this is its whole name. */
  label: string;
}

/** A 48×48 (or 56×56) round button whose name is `label`. A2's `IconButton`. */
export function IconButton({
  icon,
  label,
  variant,
  size,
  className,
  type,
  ...rest
}: IconButtonProps) {
  return (
    <BaseButton
      aria-label={label}
      className={cx(
        BASE,
        VARIANT[variant ?? "secondary"],
        ICON_SIZE[size ?? "md"],
        "px-0",
        className
      )}
      type={type ?? "button"}
      {...rest}
    >
      <Icon icon={icon} size={size === "lg" ? 24 : 20} />
    </BaseButton>
  );
}
